import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { House } from "@phosphor-icons/react";

export default function SignupPage() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    city: "",
    area: "",
    specialty: "Residential",
  });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await register(form);
      toast.success("Account created");
      nav("/dashboard");
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-ink-900 text-zinc-100">
      <div className="w-full max-w-md animate-fade-up">
        <div className="flex items-center gap-2 mb-8">
          <House size={24} weight="duotone" className="text-gold" />
          <span className="font-display text-2xl">Reagent Network</span>
        </div>

        <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-3">Join the network</div>
        <h2 className="font-display text-4xl tracking-tight">Create your account.</h2>

        <form className="mt-8 space-y-4" onSubmit={onSubmit} data-testid="signup-form">
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">Full name</Label>
            <Input
              required
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              data-testid="signup-name-input"
              className="mt-2 bg-ink-700 border-white/10 focus:border-gold focus-visible:ring-gold h-11"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">Email</Label>
            <Input
              type="email"
              required
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              data-testid="signup-email-input"
              className="mt-2 bg-ink-700 border-white/10 focus:border-gold focus-visible:ring-gold h-11"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">Password</Label>
            <Input
              type="password"
              required
              minLength={6}
              value={form.password}
              onChange={(e) => set("password", e.target.value)}
              data-testid="signup-password-input"
              className="mt-2 bg-ink-700 border-white/10 focus:border-gold focus-visible:ring-gold h-11"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">City</Label>
              <Input
                placeholder="Mumbai"
                value={form.city}
                onChange={(e) => set("city", e.target.value)}
                data-testid="signup-city-input"
                className="mt-2 bg-ink-700 border-white/10 focus:border-gold focus-visible:ring-gold h-11"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">Area</Label>
              <Input
                placeholder="Bandra West"
                value={form.area}
                onChange={(e) => set("area", e.target.value)}
                data-testid="signup-area-input"
                className="mt-2 bg-ink-700 border-white/10 focus:border-gold focus-visible:ring-gold h-11"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">Specialty</Label>
            <Select value={form.specialty} onValueChange={(v) => set("specialty", v)}>
              <SelectTrigger data-testid="signup-specialty-select" className="mt-2 bg-ink-700 border-white/10 h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Residential">Residential</SelectItem>
                <SelectItem value="Luxury">Luxury</SelectItem>
                <SelectItem value="Commercial">Commercial</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {err && <div className="text-sm text-red-400" data-testid="signup-error">{err}</div>}
          <Button
            type="submit"
            disabled={loading}
            data-testid="signup-submit-button"
            className="w-full h-11 bg-gold text-ink-900 hover:bg-gold-light font-semibold"
          >
            {loading ? "Creating..." : "Create account →"}
          </Button>
        </form>

        <div className="mt-6 text-sm text-zinc-500">
          Already have an account?{" "}
          <Link to="/login" className="text-gold hover:text-gold-light" data-testid="go-to-login">
            Sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
